import React, {useState} from "react"
import {CredentialResponse, GoogleLogin} from "@react-oauth/google"
import {useMutation, useQuery} from "@apollo/client"

import {saveLoginToken} from "helpers/User"
import userLoginByGoogleToken from "graphql/User/mutation/userLoginByGoogleToken"
import userProfileQuery from "graphql/User/query/userProfile"
import logo from "static/img/logo.png"

import "./style.scss"

type LoginFormProps = {
  loading?: boolean
}

export const LoginForm = ({loading = false}: LoginFormProps) => {
  const [error, setError] = useState("")
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [mutate] = useMutation(userLoginByGoogleToken)
  const {refetch} = useQuery(userProfileQuery)

  const handleLogin = async (tokenId: string) => {
    setError("")
    setIsSigningIn(true)

    try {
      const response = await mutate({variables: {token: tokenId}})
      const {loginToken} = response?.data || {}
      const {token, expiration} = loginToken || {}

      if (!token) throw new Error("No login token returned")
      saveLoginToken(token, expiration)
      await refetch()
    } catch (exception) {
      setError("La connexion a échoué. Vérifiez que ce compte est autorisé.")
      setIsSigningIn(false)
    }
  }

  const handleGoogleLogin = async ({credential}: CredentialResponse) => {
    if (credential) await handleLogin(credential)
  }

  const isLoading = loading || isSigningIn

  return <main className="login-screen">
    <section className="login-showcase" aria-label="Présentation de Zipflix">
      <div className="login-brand">
        <img src={logo} alt="Zipflix" />
        <span>Vidéothèque privée</span>
      </div>

      <div className="login-showcase-copy">
        <span className="login-eyebrow">Votre cinéma, chez vous</span>
        <h1>Vos histoires.<br/><em>Votre écran.</em></h1>
        <p>Retrouvez vos films, vos séries et vos collections dans une expérience conçue pour vous.</p>
        <div className="login-feature-row" aria-label="Fonctionnalités">
          <span><i className="fas fa-play" aria-hidden="true"></i>Lecture instantanée</span>
          <span><i className="fas fa-lock" aria-hidden="true"></i>Accès privé</span>
        </div>
      </div>

      <div className="login-film-wall" aria-hidden="true">
        <div className="login-poster poster-one"><span>Films</span></div>
        <div className="login-poster poster-two"><span>Séries</span></div>
        <div className="login-poster poster-three"><span>Collections</span></div>
      </div>

      <span className="login-showcase-footer">Une sélection personnelle, toujours disponible.</span>
    </section>

    <section className="login-access-panel">
      <div className="login-mobile-brand"><img src={logo} alt="Zipflix" /></div>
      <div className="login-card">
        <span className="login-card-icon fas fa-user-lock" aria-hidden="true"></span>
        <span className="login-eyebrow">Espace membre</span>
        <h2>Heureux de vous revoir</h2>
        <p>Connectez-vous avec le compte Google autorisé pour accéder à votre bibliothèque.</p>

        <div className={`google-login-shell ${isLoading ? "is-loading" : ""}`}>
          {!isLoading && <GoogleLogin
            onSuccess={handleGoogleLogin}
            onError={() => setError("Google n'a pas pu ouvrir la connexion. Réessayez dans un instant.")}
            theme="filled_black"
            size="large"
            shape="rectangular"
            text="signin_with"
            logo_alignment="left"
            width="320"
          />}
          {isLoading && <div className="login-progress" role="status" aria-live="polite">
            <span className="icon-spinner" aria-hidden="true"></span>
            <span>{isSigningIn ? "Connexion en cours…" : "Vérification de votre session…"}</span>
          </div>}
        </div>

        {error && <div className="login-error" role="alert"><span className="fas fa-exclamation-circle" aria-hidden="true"></span>{error}</div>}

        <div className="login-security-note">
          <span className="fas fa-shield-alt" aria-hidden="true"></span>
          <span><strong>Connexion sécurisée</strong><small>Zipflix ne reçoit jamais votre mot de passe Google.</small></span>
        </div>
      </div>

      <footer className="login-access-footer">
        <span>ZIPFLIX</span>
        <span className="footer-dot"></span>
        <span>Accès privé</span>
      </footer>
    </section>
  </main>
}
